import { useEffect, useRef, useState } from "react";
import { ShieldCheck, Globe2, Zap, BarChart3, Headset, IndianRupee, Truck, Radar } from "lucide-react";
import MovingBg, { BG } from "../MovingBg";

export function WhySection() {
  const items = [
    { icon: IndianRupee, title: "Competitive Rates", desc: "Multi-courier rate comparison ensures you always ship at the sharpest price." },
    { icon: Truck, title: "17+ Courier Partners", desc: "Delhivery, Blue Dart, Xpressbees, DHL, FedEx and more on one platform." },
    { icon: Zap, title: "Fast Pickup", desc: "Same-day pickup scheduling across metro cities and next-day nationwide." },
    { icon: Radar, title: "Real-Time Tracking", desc: "Scan-level visibility from booking to proof of delivery." },
    { icon: BarChart3, title: "Technology Powered", desc: "Smart courier allocation, NDR automation and delivery analytics." },
    { icon: Headset, title: "Dedicated Support", desc: "Human support on phone, WhatsApp and tickets — not just bots." },
    { icon: Globe2, title: "Domestic + International", desc: "29,000+ Indian pincodes and 220+ countries from a single dashboard." },
    { icon: ShieldCheck, title: "Transparent Management", desc: "Clear invoices, GST-compliant billing and complete shipment audit trail." },
  ];
  return (
    <section id="why" className="tri-wash relative overflow-hidden py-20">
      <MovingBg src={BG.parcels} anim="kenburns-b" opacity={0.13} />
      <div className="relative mx-auto max-w-7xl px-4 sm:px-6">
      <p className="text-xs font-bold uppercase tracking-[0.2em] text-saffron">Why INDIAFAST</p>
      <h2 className="mt-2 font-heading text-2xl font-bold tracking-tight text-navy sm:text-3xl lg:text-4xl">
        Built for serious shippers
      </h2>
      <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
        {items.map((item, i) => (
          <div key={item.title} data-testid={`why-card-${i}`}
               className="group rounded-2xl border border-slate-200 bg-white p-6 transition-all hover:-translate-y-1 hover:border-saffron/40 hover:shadow-xl hover:shadow-saffron/5">
            <span className="grid h-11 w-11 place-items-center rounded-xl bg-saffron-light text-saffron transition-colors group-hover:bg-saffron group-hover:text-white">
              <item.icon className="h-5 w-5" />
            </span>
            <h3 className="mt-4 font-heading text-lg font-semibold text-navy">{item.title}</h3>
            <p className="mt-1.5 text-sm leading-relaxed text-slate-600">{item.desc}</p>
          </div>
        ))}
      </div>
      </div>
    </section>
  );
}

export function HowItWorks() {
  const steps = [
    { n: "01", title: "Book Shipment", desc: "Enter pickup, destination and package details in under a minute." },
    { n: "02", title: "Choose Service", desc: "Compare live courier rates and pick price, speed or performance." },
    { n: "03", title: "Pickup", desc: "A courier executive collects the parcel from your doorstep." },
    { n: "04", title: "Track", desc: "Follow every scan in real time with proactive notifications." },
    { n: "05", title: "Delivered", desc: "Proof of delivery captured digitally. COD settled to your wallet." },
  ];
  return (
    <section className="tri-wash-alt relative overflow-hidden py-20">
      <MovingBg src={BG.highway} anim="kenburns-a" opacity={0.12} />
      <div className="relative mx-auto max-w-7xl px-4 sm:px-6">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-saffron">How INDIAFAST Works</p>
        <h2 className="mt-2 font-heading text-2xl font-bold tracking-tight text-navy sm:text-3xl lg:text-4xl">Five steps to delivered</h2>
        <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-5">
          {steps.map((s, i) => (
            <div key={s.n} data-testid={`how-step-${i}`} className="relative">
              <p className="font-heading text-5xl font-extrabold text-saffron/15">{s.n}</p>
              <div className="absolute left-0 top-3 h-2 w-2 rounded-full bg-saffron" />
              <h3 className="mt-4 font-heading text-lg font-bold text-navy">{s.title}</h3>
              <p className="mt-1.5 text-sm text-slate-600">{s.desc}</p>
              {i < steps.length - 1 && <div className="absolute -right-3 top-4 hidden h-px w-6 bg-slate-300 lg:block" />}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export function SolutionsSection({ solutions }) {
  const fallback = [
    { title: "eCommerce Sellers", desc: "Bulk shipping, COD, and NDR automation for marketplace and D2C orders." },
    { title: "SMEs & Enterprises", desc: "Credit terms, dedicated account managers, and API-first logistics." },
    { title: "Exporters & Importers", desc: "Door-to-door international shipping with customs documentation support." },
    { title: "NRI Customers", desc: "Send documents, gifts, and personal effects worldwide with full tracking." },
  ];
  const items = solutions?.length ? solutions : fallback;
  return (
    <section className="relative overflow-hidden bg-navy py-20 text-white">
      <div className="pointer-events-none absolute inset-0" aria-hidden="true">
        <img src={BG.port} alt="" loading="lazy" className="kenburns-b h-full w-full object-cover opacity-15" />
        <div className="absolute inset-0 bg-navy/70" />
      </div>
      <div className="relative mx-auto max-w-7xl px-4 sm:px-6">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-saffron">Business Solutions</p>
        <h2 className="mt-2 font-heading text-2xl font-bold tracking-tight sm:text-3xl lg:text-4xl">One platform. Every kind of shipper.</h2>
        <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {items.map((s, i) => (
            <div key={s.title} data-testid={`solution-card-${i}`}
                 className="rounded-2xl border border-white/10 bg-navy-soft p-6 transition-colors hover:border-saffron/50">
              <h3 className="font-heading text-lg font-bold text-white">{s.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-slate-400">{s.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export function useCountUp(target, start, duration = 1600) {
  const [value, setValue] = useState(0);
  useEffect(() => {
    if (!start) return;
    let raf;
    const t0 = performance.now();
    const tick = (t) => {
      const p = Math.min((t - t0) / duration, 1);
      setValue(Math.round(target * (1 - Math.pow(1 - p, 3))));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [start, target, duration]);
  return value;
}

export function useInView() {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => e.isIntersecting && setInView(true), { threshold: 0.3 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  return [ref, inView];
}
